import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { type QuizSet } from '@/types';
import { useCreateAssignmentDialog } from './useCreateAssignmentDialog';
import { useIsInGoogleClassroom } from './useIsInGoogleClassroom';
const DIALOG_EFFECT_QUERY_PARAM_KEY = 'shouldShowCreateAssignmentDialog';

/**
 * DIALOG_EFFECT_QUERY_PARAM_KEY 쿼리 파라미터가 있을 경우, 퀴즈 생성 다이얼로그를 띄우는 effect hook입니다.
 */
export function useCreateAssignmentDialogEffect({
  quizSet
}: {
  quizSet?: QuizSet;
}) {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const isInGoogleClassroom = useIsInGoogleClassroom();
  const didShowCreateAssignmentDialog = useRef(false);
  const createAssignmentDialog = useCreateAssignmentDialog();
  useEffect(() => {
    if (!isInGoogleClassroom || quizSet == null || didShowCreateAssignmentDialog.current) {
      return;
    }
    const shouldShowCreateAssignmentDialog = router.query[DIALOG_EFFECT_QUERY_PARAM_KEY] === 'true';
    if (shouldShowCreateAssignmentDialog) {
      const isDuplicate = router.query.isDuplicate === 'true';
      // FIXME: 사용자가 rAF 신경 쓸 필요 없도록 rAF overlay wrapper 만들기
      // Note: 진입 직후에 overlay.open 를 호출하면 Overlay 렌더링이 그냥 먹히는 경우가 있습니다.
      // overlay-kit 코드를 읽어보니 render dispatch를 requestAnimationFrame를 사용해서 지연시키고 있는데요.
      // 이로 인해 Race condition 이슈가 발생하는 것 같습니다.
      // 정확한 원인은 좀 더 코드를 읽어봐야겠지만, rAF를 사용하면 타이밍이 맞춰지면서 문제가 해결 되는 것 같습니다.
      // https://github.com/toss/overlay-kit/blob/main/packages/src/context/provider.tsx#L29
      requestAnimationFrame(() => {
        createAssignmentDialog.open({
          title: t('quizDetail.createAssignmentDialog.title'),
          quizSet,
          onCreateSuccess: () => {
            logger.track('GC_create_assignment', {
              location: isDuplicate ? 'duplicate' : 'detail',
              quizSetId: quizSet.id,
              title: quizSet.title
            });
          }
        });
      });
      didShowCreateAssignmentDialog.current = true;
      void router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          [DIALOG_EFFECT_QUERY_PARAM_KEY]: false
        }
      }, undefined, {
        shallow: true
      });
    }
  }, [createAssignmentDialog, isInGoogleClassroom, quizSet, router, t]);
}
useCreateAssignmentDialogEffect.KEY = DIALOG_EFFECT_QUERY_PARAM_KEY;